/**
 * @file CountryDropdown.js
 * @description A dropdown component for selecting a country, sourced from react-phone-number-input's bundled ISO country list.
 */

import React from 'react';
import { getCountries } from 'react-phone-number-input';
import countryNames from 'react-phone-number-input/locale/en.json';
import { Dropdown } from '../../helpers/Dropdown';

/**
 * A dropdown component for selecting a country.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - Additional CSS class for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines if the label should be displayed inline.
 * @param {string} props.selectedCountry - The currently selected ISO 3166-1 alpha-2 country code.
 * @param {function} props.onCountryChange - The callback function to be called when the country selection changes.
 * @param {string} [props.label="Country"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines if the dropdown is required.
 * @returns {JSX.Element} The rendered component.
 */
const CountryDropdown = ({
    className,
    inlineLabel = true,
    selectedCountry,
    onCountryChange,
    label = 'Country',
    isRequired = false,
}) => {
    const countryCodes = getCountries();

    const getDefaultLabel = () =>
        countryNames[selectedCountry] || 'Select Country';

    const countryOptions = countryCodes
        .map(code => ({
            value: code,
            label: countryNames[code] || code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    if (!isRequired)
        countryOptions.unshift({ value: '', label: 'Select Country' });

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="country-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <Dropdown
                id="country-dropdown"
                defaultValue={getDefaultLabel()}
                options={countryOptions}
                allowSearch
                value={selectedCountry}
                onChange={onCountryChange}
            />
        </div>
    );
};

export default CountryDropdown;
